import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/api';
import { trackGuidanceOpened } from '../utils/analytics';
import axios from 'axios';
import { 
  Sparkle, 
  Lock, 
  CaretDown, 
  CaretUp,
  CircleNotch
} from '@phosphor-icons/react';

export function GuidanceCard({ 
  contextType = 'checklist_step',
  contextId = null,
  stepTitle = null,
  stepDescription = null,
  compact = false 
}) {
  const { language } = useLanguage();
  const { isPaid } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const translations = {
    en: {
      title: "Need help with this step?",
      subtitle: "I'll help you understand this and what to do next.",
      dashboardTitle: "Need help with your next step?",
      dashboardSubtitle: "Get personalized guidance on what to do.",
      whatNeed: "What do I need?",
      whatFirst: "What should I do first?",
      explain: "Explain simply",
      nextStep: "What should I do next?",
      myPlan: "Explain my plan",
      missing: "What am I missing?",
      askGuidance: "Ask PaperPal Guidance",
      loading: "Getting guidance...",
      premiumLocked: "Unlock personalized guidance",
      yourNextStep: "Your next step:",
      whatToDo: "What to do:",
      close: "Close"
    },
    fr: {
      title: "Besoin d'aide avec cette étape?",
      subtitle: "Je vous aide à comprendre et à savoir quoi faire ensuite.",
      dashboardTitle: "Besoin d'aide pour votre prochaine étape?",
      dashboardSubtitle: "Obtenez des conseils personnalisés.",
      whatNeed: "De quoi j'ai besoin?",
      whatFirst: "Par où commencer?",
      explain: "Expliquer simplement",
      nextStep: "Quoi faire ensuite?",
      myPlan: "Expliquer mon plan",
      missing: "Qu'est-ce que j'oublie?",
      askGuidance: "Demander à PaperPal",
      loading: "Chargement...",
      premiumLocked: "Débloquer les conseils personnalisés",
      yourNextStep: "Votre prochaine étape:",
      whatToDo: "Ce qu'il faut faire:",
      close: "Fermer"
    }
  };

  const t = translations[language] || translations.en;

  const quickButtons = contextType === 'dashboard' 
    ? [
        { key: 'next_step', label: t.nextStep },
        { key: 'my_plan', label: t.myPlan },
        { key: 'missing', label: t.missing }
      ]
    : [
        { key: 'what_need', label: t.whatNeed },
        { key: 'what_first', label: t.whatFirst },
        { key: 'explain', label: t.explain }
      ];

  const handleQuickQuestion = async (questionType) => {
    setIsLoading(true);
    setError(null);
    setIsExpanded(true);

    // Track guidance opened
    trackGuidanceOpened(questionType, contextType);

    try {
      const params = new URLSearchParams({
        question_type: questionType,
        context_type: contextType,
        language: language
      });
      
      if (contextId) params.append('context_id', contextId);
      if (stepTitle) params.append('step_title', stepTitle);
      if (stepDescription) params.append('step_description', stepDescription);

      const res = await axios.post(`${API}/guidance/quick?${params.toString()}`, {});

      setResponse(res.data);
    } catch (err) {
      console.error('Guidance error:', err);
      setError(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load guidance');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAskGuidance = () => {
    handleQuickQuestion(contextType === 'dashboard' ? 'next_step' : 'explain');
  };

  return (
    <div 
      className="w-full rounded-xl bg-[#F8F9FB] border border-pp-border p-4 md:p-5 my-6"
      data-testid="guidance-card"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-pp-navy/10 flex items-center justify-center flex-shrink-0">
          <Sparkle size={18} className="text-pp-navy" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-pp-text text-sm md:text-base">
            {contextType === 'dashboard' ? t.dashboardTitle : t.title}
          </h4>
          <p className="text-xs md:text-sm text-pp-text-muted mt-0.5">
            {contextType === 'dashboard' ? t.dashboardSubtitle : t.subtitle}
          </p>
        </div>
      </div>

      {/* Quick Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {quickButtons.map((btn) => (
          <button
            key={btn.key}
            onClick={() => handleQuickQuestion(btn.key)}
            disabled={isLoading}
            className="h-9 px-3 rounded-lg bg-white border border-pp-border text-sm text-pp-text hover:bg-pp-secondary/50 transition-colors disabled:opacity-50"
            data-testid={`guidance-btn-${btn.key}`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Main CTA */}
      {!isExpanded && (
        <button
          onClick={handleAskGuidance}
          disabled={isLoading}
          className="w-full h-11 rounded-lg bg-pp-navy text-white font-medium text-sm hover:bg-pp-navy-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="guidance-ask-btn"
        >
          {isLoading ? (
            <>
              <CircleNotch size={18} className="animate-spin" />
              {t.loading}
            </>
          ) : (
            t.askGuidance
          )}
        </button>
      )}

      {/* Expanded Response Area */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 overflow-hidden"
          >
            <div className="bg-white rounded-lg border border-pp-border p-4 max-h-[350px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch size={24} className="animate-spin text-pp-navy" />
                  <span className="ml-2 text-pp-text-muted">{t.loading}</span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-pp-error text-sm">{error}</p>
                </div>
              ) : response ? (
                <div className="space-y-4">
                  {/* Explanation */}
                  <p className="text-pp-text text-sm leading-relaxed">
                    {response.explanation}
                  </p>

                  {/* Actions */}
                  {response.actions && response.actions.length > 0 && (
                    <div>
                      <p className="font-medium text-pp-text text-sm mb-2">
                        👉 {t.whatToDo}
                      </p>
                      <ul className="space-y-1.5">
                        {response.actions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-pp-text-secondary">
                            <span className="text-pp-navy mt-0.5">•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Next Step - Premium only */}
                  {isPaid && response.next_step ? (
                    <div className="pt-3 border-t border-pp-border">
                      <p className="font-medium text-pp-text text-sm mb-1">
                        👉 {t.yourNextStep}
                      </p>
                      <p className="text-sm text-pp-success-text bg-pp-success-muted p-3 rounded-lg">
                        {response.next_step}
                      </p>
                    </div>
                  ) : !isPaid && (
                    <div className="pt-3 border-t border-pp-border">
                      <p className="font-medium text-pp-text text-sm mb-2">
                        👉 {t.yourNextStep}
                      </p>
                      <div className="relative">
                        <div className="bg-pp-secondary/50 p-3 rounded-lg blur-[2px] select-none">
                          <p className="text-sm text-pp-text-muted">
                            {language === 'fr' 
                              ? 'Votre prochaine action personnalisée sera affichée ici...'
                              : 'Your personalized next action will appear here...'}
                          </p>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-pp-border shadow-sm">
                            <Lock size={14} className="text-pp-text-muted" />
                            <span className="text-xs font-medium text-pp-text-muted">
                              {t.premiumLocked}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Close button */}
              {!isLoading && (
                <button
                  onClick={() => {
                    setIsExpanded(false);
                    setResponse(null);
                  }}
                  className="w-full mt-4 py-2 text-sm text-pp-text-muted hover:text-pp-text transition-colors"
                >
                  {t.close}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Compact version for dashboard sidebar
export function GuidanceCardCompact() {
  return <GuidanceCard contextType="dashboard" compact />;
}

// Document-specific guidance card
export function DocumentGuidanceCard({ documentName = null }) {
  const { language } = useLanguage();
  const { isPaid } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const translations = {
    en: {
      title: "Need help reviewing this?",
      askAbout: "Ask about this document",
      loading: "Getting guidance...",
      whatToDo: "Check:",
      yourNextStep: "Next step:",
      premiumLocked: "Unlock personalized guidance",
      close: "Close"
    },
    fr: {
      title: "Besoin d'aide pour vérifier?",
      askAbout: "Poser une question sur ce document",
      loading: "Chargement...",
      whatToDo: "À vérifier:",
      yourNextStep: "Prochaine étape:",
      premiumLocked: "Débloquer les conseils personnalisés",
      close: "Fermer"
    }
  };

  const t = translations[language] || translations.en;

  const handleAskAboutDocument = async () => {
    setIsLoading(true);
    setIsExpanded(true);

    try {
      const question = language === 'fr' 
        ? "Que dois-je vérifier dans ce document avant de le soumettre?"
        : "What should I check in this document before submitting it?";

      const res = await axios.post(`${API}/guidance/ask`, {
        context_type: 'document',
        question: question,
        language: language
      });

      setResponse(res.data);
    } catch (err) {
      console.error('Document guidance error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isPaid) return null; // Only show for premium users

  return (
    <div className="mt-6 p-4 rounded-xl bg-[#F8F9FB] border border-pp-border">
      <div className="flex items-center gap-3 mb-3">
        <Sparkle size={18} className="text-pp-navy" weight="duotone" />
        <span className="font-medium text-pp-text text-sm">{t.title}</span>
      </div>

      {!isExpanded ? (
        <button
          onClick={handleAskAboutDocument}
          disabled={isLoading}
          className="w-full h-10 rounded-lg bg-pp-navy text-white font-medium text-sm hover:bg-pp-navy-hover transition-colors"
        >
          {t.askAbout}
        </button>
      ) : (
        <div className="bg-white rounded-lg border border-pp-border p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <CircleNotch size={20} className="animate-spin text-pp-navy" />
              <span className="ml-2 text-sm text-pp-text-muted">{t.loading}</span>
            </div>
          ) : response ? (
            <div className="space-y-3">
              <p className="text-sm text-pp-text">{response.explanation}</p>
              
              {response.actions && response.actions.length > 0 && (
                <div>
                  <p className="font-medium text-pp-text text-xs mb-1">👉 {t.whatToDo}</p>
                  <ul className="space-y-1">
                    {response.actions.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-pp-text-secondary">
                        <span className="text-pp-navy">•</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {response.next_step && (
                <div className="pt-2 border-t border-pp-border">
                  <p className="font-medium text-pp-text text-xs mb-1">👉 {t.yourNextStep}</p>
                  <p className="text-xs text-pp-success-text bg-pp-success-muted p-2 rounded">
                    {response.next_step}
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setIsExpanded(false);
                  setResponse(null);
                }}
                className="w-full py-1.5 text-xs text-pp-text-muted hover:text-pp-text"
              >
                {t.close}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
